import { type ReactNode } from 'react';
import {
	Table,
	Thead,
	Tbody,
	Tr,
	Th,
	Td,
	Box,
	Flex,
	type TableProps,
} from '@chakra-ui/react';
import { useLayout } from '../../hooks/useLayout';
import { Icon } from './Icon';

/**
 * DataTable — a generic, token-driven table with sortable headers, density,
 * and a sticky header. Consolidates the repeated table scaffolding across the
 * dashboard/reporting/invoice tables.
 */
export type SortDirection = 'asc' | 'desc';
export type TableDensity = 'compact' | 'comfortable';

export interface DataTableColumn<Row> {
	key: string;
	header: ReactNode;
	render: (row: Row, index: number) => ReactNode;
	isNumeric?: boolean;
	sortable?: boolean;
	width?: string | number;
}

export interface DataTableProps<Row> extends Omit<TableProps, 'children'> {
	columns: DataTableColumn<Row>[];
	rows: Row[];
	rowKey: (row: Row, index: number) => string;
	density?: TableDensity;
	stickyHeader?: boolean;
	sortKey?: string;
	sortDirection?: SortDirection;
	onSort?: (key: string) => void;
	onRowClick?: (row: Row) => void;
	emptyState?: ReactNode;
}

const DENSITY_PADDING: Record<TableDensity, { px: number; py: number }> = {
	compact: { px: 3, py: 1.5 },
	comfortable: { px: 4, py: 3 },
};

export function DataTable<Row>({
	columns,
	rows,
	rowKey,
	density,
	stickyHeader = true,
	sortKey,
	sortDirection,
	onSort,
	onRowClick,
	emptyState,
	...tableProps
}: DataTableProps<Row>) {
	const layout = useLayout();
	const resolvedDensity = density ?? (layout === 'mobile' ? 'compact' : 'comfortable');
	const pad = DENSITY_PADDING[resolvedDensity];

	const renderSortIcon = (col: DataTableColumn<Row>) => {
		if (!col.sortable) return null;
		if (sortKey !== col.key)
			return <Icon name="arrowsSort" size="xs" ml={1} color="fg.subtle" />;
		return (
			<Icon
				name={sortDirection === 'asc' ? 'arrowUp' : 'arrowDown'}
				size="xs"
				ml={1}
				color="brand.fg"
			/>
		);
	};

	return (
		<Box overflowX="auto" maxW="100%">
		<Table size="sm" {...tableProps}>
			<Thead>
				<Tr>
					{columns.map((col) => (
						<Th
							key={col.key}
							isNumeric={col.isNumeric}
							width={col.width}
							px={pad.px}
							py={2}
							cursor={col.sortable ? 'pointer' : 'default'}
							onClick={col.sortable && onSort ? () => onSort(col.key) : undefined}
							position={stickyHeader ? 'sticky' : undefined}
							top={stickyHeader ? 0 : undefined}
							zIndex={stickyHeader ? 'stickyTableHeader' : undefined}
							bg="bg.subtle"
							boxShadow={stickyHeader ? 'tableHeader' : undefined}
							borderColor="border.subtle"
							color="fg.muted"
							textTransform="none"
							letterSpacing="normal"
							fontFamily="body"
							userSelect="none"
						>
							<Flex
								align="center"
								justify={col.isNumeric ? 'flex-end' : 'flex-start'}
								display="inline-flex"
							>
								{col.header}
								{renderSortIcon(col)}
							</Flex>
						</Th>
					))}
				</Tr>
			</Thead>
			<Tbody>
				{rows.length === 0 ? (
					<Tr>
						<Td colSpan={columns.length} border="none">
							<Box py={8} textAlign="center" color="fg.muted">
								{emptyState ?? 'No data'}
							</Box>
						</Td>
					</Tr>
				) : (
					rows.map((row, index) => (
						<Tr
							key={rowKey(row, index)}
							cursor={onRowClick ? 'pointer' : 'default'}
							onClick={onRowClick ? () => onRowClick(row) : undefined}
							_hover={onRowClick ? { bg: 'bg.subtle' } : undefined}
						>
							{columns.map((col) => (
								<Td
									key={col.key}
									isNumeric={col.isNumeric}
									px={pad.px}
									py={pad.py}
									borderColor="border.subtle"
									color="fg.default"
								>
									{col.render(row, index)}
								</Td>
							))}
						</Tr>
					))
				)}
			</Tbody>
		</Table>
		</Box>
	);
}

export default DataTable;
